<template>
  <div class="star-rating-filter">
    <div class="star-rating-selector">
      <div
        v-for="star in 5"
        :key="star"
        :class="[
          'star-item',
          {
            'active': star <= (rating || 0),
            'clickable': star >= minStars,
            'disabled': star < minStars
          }
        ]"
        @click="selectRating(star)"
        @mouseenter="hoveredRating = star"
        @mouseleave="hoveredRating = null"
        :title="star < minStars ? `${minStars} stars and above only` : `${star} stars and above`"
      >
        <svg
          class="star-icon"
          viewBox="0 0 24 24"
          :fill="(star <= (hoveredRating || rating || 0)) ? '#FFD700' : '#E5E5E5'"
          stroke="#DDD"
          stroke-width="1"
        >
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
        </svg>
      </div>
    </div>
    <div v-if="rating" class="rating-description">
      {{ rating }}+ {{ $t('stars and above') }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import { useSearchStore } from "~/store/search";
import { useRoute } from "vue-router";

const route = useRoute();
const searchStore = useSearchStore();
const rating = ref<number | null>(null);
const hoveredRating = ref<number | null>(null);
const defaultRating = searchStore.filters?.rating || route.query.rating_gte || null;

// Brand-scoped floor (e.g. Solaire = 4★+): stars below this are not selectable.
const minStars = (useBrand().feature('minHotelStars') as number) || 0;

const emit = defineEmits(['update']);

onMounted(() => {
  rating.value = +defaultRating || null;
});

function selectRating(selectedRating: number) {
  // Brand floor: never allow selecting below the minimum.
  if (selectedRating < minStars) return;
  // If clicking the same rating, clear it; otherwise set the new rating
  if (rating.value === selectedRating) {
    rating.value = null;
  } else {
    rating.value = selectedRating;
  }
  updateRating();
}

function clearRating() {
  rating.value = null;
  updateRating();
}

function updateRating() {
  emit('update', rating.value);
}

watch(
  () => route.query.rating_gte,
  (value) => {
    rating.value = value ? +value : null;
  }
);

</script>

<style scoped lang="scss">
.star-rating-filter {
  display: flex;
  flex-direction: column;
  gap: 12px;

  .star-rating-label {
    font-weight: 600;
    font-size: 14px;
    color: #333;
    margin-bottom: 4px;
  }

  .star-rating-selector {
    display: flex;
    align-items: center;
    gap: 4px;

    .star-item {
      cursor: pointer;
      transition: all 0.2s ease;
      padding: 4px;
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;

      &:hover {
        background-color: rgba(25, 133, 249, 0.1);
        transform: scale(1.05);
      }

      &.active {
        .star-icon {
          fill: #FFD700;
          stroke: #FFA500;
        }
      }

      &.disabled {
        cursor: not-allowed;
        opacity: 0.35;
        pointer-events: none;
      }

      .star-icon {
        width: 24px;
        height: 24px;
        transition: all 0.2s ease;
        fill: #E5E5E5;
        stroke: #DDD;
        stroke-width: 1;
      }
    }

    .clear-rating-btn {
      margin-left: 12px;
      background: none;
      border: none;
      color: #1985F9;
      cursor: pointer;
      font-size: 12px;
      text-decoration: underline;
      padding: 4px 8px;
      border-radius: 4px;
      transition: all 0.2s ease;

      &:hover {
        color: #0d5cb5;
        background-color: rgba(25, 133, 249, 0.1);
        text-decoration: none;
      }
    }
  }

  .rating-description {
    font-size: 12px;
    color: #666;
    font-style: italic;
    margin-top: 4px;
    display: flex;
    align-items: center;
    gap: 4px;

    &::before {
      content: "⭐";
      font-size: 14px;
    }
  }
}

// Mobile responsive
@media (max-width: 768px) {
  .star-rating-filter {
    .star-rating-selector {
      gap: 2px;

      .star-item {
        padding: 2px;

        .star-icon {
          width: 20px;
          height: 20px;
        }
      }

      .clear-rating-btn {
        margin-left: 8px;
        font-size: 11px;
        padding: 2px 6px;
      }
    }
  }
}
</style>
